/**
 * The print view every report shares (roadmap RP-7: "PDF, print-optimized").
 *
 * Wraps a report body in the app's print shell — US Letter, light theme for
 * ink — and lets the browser's Print dialog make the PDF. Charts inside are
 * drawn at a fixed width (the bodies take `print`), so nothing depends on a
 * measured container. Plain flow rather than Paged.js: a chart is one block
 * and must not be split across a page boundary mid-SVG.
 */

import type { ReactNode } from 'react';
import { PrintChrome } from '@/components/print/PrintChrome';

export interface ReportPrintShellProps {
  /** The browser tab and the PDF's default file name. */
  title: string;
  /** Where "Back" leads — the live report, controls included. */
  backHref: string;
  /** Shown under the title: range, segment, when. */
  meta?: string;
  children: ReactNode;
}

export const REPORT_PRINT_WIDTH_PX = 680;

export function ReportPrintShell({ title, backHref, meta, children }: ReportPrintShellProps) {
  return (
    <PrintChrome
      documentTitle={title}
      pageSizeCss="letter"
      previewMaxWidthMm={216}
      backHref={backHref}
      backLabel="Back to report"
      usePagedJs={false}
    >
      <div className="space-y-6" style={{ width: `${REPORT_PRINT_WIDTH_PX}px`, maxWidth: '100%' }}>
        <header>
          <p className="font-mono text-[0.68rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">Priorities.ai · Report</p>
          <h1 className="mt-1 text-[1.75rem] leading-tight text-[var(--text-pri)]" style={{ fontFamily: 'var(--serif)', fontWeight: 400 }}>{title}</h1>
          {meta && <p className="mt-1 text-sm text-[var(--text-sec)]">{meta}</p>}
        </header>
        {children}
      </div>
    </PrintChrome>
  );
}
